import { useEffect, useState, useCallback } from 'react'
import { Product } from './useProductScanner'

interface ToastData {
  name: string
  city: string
  product: Product
}

const NAMES = [
  'Michael',
  'Sarah',
  'David',
  'Jennifer',
  'James',
  'Emily',
  'Robert',
  'Jessica',
  'John',
  'Ashley',
  'William',
  'Amanda',
  'Richard',
  'Melissa',
  'Joseph',
  'Nicole',
  'Thomas',
  'Stephanie',
  'Christopher',
  'Rebecca',
  'Daniel',
  'Laura',
  'Matthew',
  'Rachel',
  'Anthony',
  'Kimberly',
  'Mark',
  'Lauren',
  'Donald',
  'Amy',
  'Steven',
  'Brittany',
  'Carlos',
  'Maria',
  'Jose',
  'Ana',
  'Luis',
  'Carmen',
  'Miguel',
  'Rosa',
  'Juan',
  'Patricia',
  'Francisco',
  'Linda',
  'Antonio',
  'Sofia',
  'Jorge',
  'Isabella',
]

const CITIES = [
  'New York',
  'Los Angeles',
  'Chicago',
  'Houston',
  'Phoenix',
  'Philadelphia',
  'San Antonio',
  'San Diego',
  'Dallas',
  'San Jose',
  'Austin',
  'Jacksonville',
  'Fort Worth',
  'Columbus',
  'Charlotte',
  'San Francisco',
  'Indianapolis',
  'Seattle',
  'Denver',
  'Boston',
  'Nashville',
  'Detroit',
  'Portland',
  'Las Vegas',
  'Memphis',
  'Louisville',
  'Baltimore',
  'Milwaukee',
  'Albuquerque',
  'Tucson',
  'Fresno',
  'Sacramento',
  'Kansas City',
  'Atlanta',
  'Miami',
  'Raleigh',
]

export function useRecentPurchaseToast(products: Product[]) {
  const [currentToast, setCurrentToast] = useState<ToastData | null>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [nameHistory, setNameHistory] = useState<string[]>([])

  const selectRandomProduct = useCallback(() => {
    if (products.length === 0) return null

    // If using fallback products (Pack Despertar / Edición Digital), use 70/30 distribution
    const isFallback = products.some(
      (p) => p.title === 'Pack Despertar' || p.title === 'Edición Digital',
    )

    if (isFallback && products.length === 2) {
      const rand = Math.random()
      return rand < 0.7 ? products[0] : products[1]
    }

    // Otherwise, random selection
    return products[Math.floor(Math.random() * products.length)]
  }, [products])

  const selectRandomName = useCallback(() => {
    // Filter out names from recent history
    const availableNames = NAMES.filter((name) => !nameHistory.includes(name))

    // If all names are in history, reset and use any name
    const namePool = availableNames.length > 0 ? availableNames : NAMES

    return namePool[Math.floor(Math.random() * namePool.length)]
  }, [nameHistory])

  const selectRandomCity = useCallback(() => {
    return CITIES[Math.floor(Math.random() * CITIES.length)]
  }, [])

  const showToast = useCallback(() => {
    const product = selectRandomProduct()
    if (!product) return

    const name = selectRandomName()
    const city = selectRandomCity()

    setCurrentToast({ name, city, product })
    setIsVisible(true)

    // Update name history (keep last 10)
    setNameHistory((prev) => {
      const updated = [...prev, name]
      return updated.slice(-10)
    })

    // Hide after 4 seconds
    setTimeout(() => {
      setIsVisible(false)
      setTimeout(() => setCurrentToast(null), 300) // Wait for exit animation
    }, 4000)
  }, [selectRandomProduct, selectRandomName, selectRandomCity])

  useEffect(() => {
    if (products.length === 0) return

    // First toast after 8 seconds
    const initialTimeout = setTimeout(() => {
      showToast()
    }, 8000)

    // Repeat forever at random intervals (20-35 seconds)
    const scheduleNext = () => {
      const delay = 20000 + Math.random() * 15000 // 20-35 seconds
      return setTimeout(() => {
        showToast()
        scheduleNext()
      }, delay)
    }

    // Start the recurring cycle after first toast + 4s visible + random delay
    const recurringTimeout = setTimeout(
      () => {
        scheduleNext()
      },
      8000 + 4000 + 20000,
    )

    return () => {
      clearTimeout(initialTimeout)
      clearTimeout(recurringTimeout)
    }
  }, [products, showToast])

  return { currentToast, isVisible }
}
